import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Form, Select } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import generateTestId from 'packages/helpers/testIdHelpers';
import type { FontFamily as FontFamilyType } from '../../Shots/Text/Fonts/types';

type Props = {
    fontIdData: [string, boolean, string];
    rawFontIds: string[];
    onFontChange: (fontId: string) => void;
    disabled?: boolean;
    locked?: boolean;
};

// TODO: Make text fonts' components to use these generice ones after release
const FontFamilyBase = ({ fontIdData, rawFontIds, onFontChange, disabled, locked }: Props) => {
    const [, , mixedValue] = fontIdData;
    const [searchValue, setSearchValue] = useState('');
    const fonts: FontFamilyType[] = useSelector(fontsElementsSelector);
    const { translate } = useTranslate();

    const fontsList: Record<string, FontFamilyType> = useMemo(() => {
        const temp: Record<string, FontFamilyType> = {};

        fonts.forEach((font) => {
            temp[font.name] = font;
        });

        return temp;
    }, [fonts]);

    const dupliacateFonts: FontFamilyType[] = useMemo(() => {
        return fonts.filter((f) => fontsList[f.name]);
    }, [fonts, fontsList]);

    const [fontFamilyIds, familyName] = useMemo(() => {
        const fontFamilyIds = new Set();
        let familyName = '';

        Object.values(fontsList).forEach(({ id, name, fonts }) => {
            if (fonts.some((f) => rawFontIds.includes(String(f.id)))) {
                familyName = familyName || name;
                fontFamilyIds.add(id);
            } else {
                const duplicate = dupliacateFonts.find((f) => f.fonts.some((ff) => rawFontIds.includes(String(ff.id))));
                familyName = duplicate ? duplicate.name : 'Default';
            }
        });

        if (rawFontIds.length > 1) {
            familyName = mixedValue;
        }

        return [fontFamilyIds, familyName];
    }, [rawFontIds, fontsList, mixedValue, dupliacateFonts]);

    const isFontMissing = () => {
        const allFontsListIds = Object.values(fontsList).flatMap((family) => family.fonts.map((f) => f.id.toString()));
        const allDuplicateFontIds = dupliacateFonts.flatMap((f) => f.fonts.map((ff) => ff.id.toString()));

        const diff = rawFontIds.filter((id) => !allFontsListIds.includes(id) && !allDuplicateFontIds.includes(id));

        return diff.length > 0;
    };

    const familyListArray = useMemo(() => {
        if (searchValue === '') {
            return Object.values(fontsList);
        }

        return Object.values(fontsList).filter((f) => f.name.toLowerCase().includes(searchValue.toLowerCase()));
    }, [fontsList, searchValue]);

    const handleChange = useCallback(
        (family: FontFamilyType) => {
            const defaultFont = family.fonts.find((f) => f.style === 'REGULAR') || family.fonts[0];

            onFontChange(defaultFont.id.toString());
        },
        [onFontChange],
    );

    return (
        <Form.Group>
            <Dropdown
                minWidth="288px"
                onClose={() => setSearchValue('')}
                {...generateTestId('shots_text_typeface')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_typeface"
                        isPressed={isOpen}
                        rightIcon={<Dropdown.Arrow />}
                        isDisabled={disabled || locked}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>{familyName}</Dropdown.SelectButtonText>
                    </Button>
                )}
            >
                <Select.SearchInput
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={translate('editor.sidebar.shots.fonts.fontfamily.search')}
                    aria-label={translate('editor.sidebar.shots.fonts.fontfamily.search.label')}
                />
                {familyListArray.map((family) => (
                    <Dropdown.Item
                        key={family.id}
                        onClick={() => handleChange(family)}
                        isChecked={fontFamilyIds.has(family.id)}
                    >
                        {family.name}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            {isFontMissing() && (
                <Form.HelperText icon={<Form.IconError />}>
                    {translate('editor.sidebar.shots.fonts.fontfamily.missing')}
                </Form.HelperText>
            )}
        </Form.Group>
    );
};

export { FontFamilyBase };
